import { MotionValue, motion, useSpring, useTransform } from "motion/react";
import type React from "react";
import { Fragment, useEffect, useState } from "react";

import "./Counter.css";
import { cn } from "@/lib/utils";
import { Text } from "@/app/v2/_components/text/Text";
import { Confetti } from "@neoconfetti/react";

type PlaceValue = number | ".";

interface NumberProps {
  mv: MotionValue<number>;
  number: number;
  height: number;
  fontClassName?: string;
}

function Number({ mv, number, height, fontClassName }: NumberProps) {
  const y = useTransform(mv, (latest) => {
    const placeValue = latest % 10;
    const offset = (10 + number - placeValue) % 10;
    let memo = offset * height;
    if (offset > 5) {
      memo -= 10 * height;
    }
    return memo;
  });

  return (
    <motion.span className={cn("counter-number")} style={{ y }}>
      <Text className={fontClassName}>{number}</Text>
    </motion.span>
  );
}

function normalizeNearInteger(num: number): number {
  const nearest = Math.round(num);
  const tolerance = 1e-9 * Math.max(1, Math.abs(num));
  return Math.abs(num - nearest) < tolerance ? nearest : num;
}

function getValueRoundedToPlace(value: number, place: number): number {
  const scaled = value / place;
  return Math.floor(normalizeNearInteger(scaled));
}

interface DigitProps {
  place: PlaceValue;
  value: number;
  height: number;
  digitStyle?: React.CSSProperties;
  digitClassName?: string;
  fontClassName?: string;
  onDoneAnimating?: (isDone: boolean) => void;
}

function Digit({ place, value, height, digitStyle, digitClassName, fontClassName, onDoneAnimating }: DigitProps) {
  if (place === ".") {
    return (
      <span className={cn("counter-digit", digitClassName)} style={{ height, ...digitStyle, width: "fit-content" }}>
        .
      </span>
    );
  }

  const valueRoundedToPlace = getValueRoundedToPlace(value, place);

  // Make higher place orders respond more slowly so the animation "cascades"
  // from lower orders (1s/10s) to higher magnitudes.
  const rawOrder = Math.round(Math.log10(place)); // place is expected to be powers of 10 (>= 1 for integer digits)
  const order = Math.max(0, rawOrder);
  const stiffness = 260 / (order + 1);
  const animatedValue = useSpring(valueRoundedToPlace, { stiffness, damping: 20, mass: 0.7 });

  if (valueRoundedToPlace === value) {
    onDoneAnimating?.(true);
  } else {
    onDoneAnimating?.(false);
  }

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace]);

  return (
    <span className={cn("counter-digit", fontClassName, digitClassName)} style={{ height, ...digitStyle }}>
      {Array.from({ length: 10 }, (_, i) => (
        <Number key={i} mv={animatedValue} number={i} height={height} fontClassName={fontClassName} />
      ))}
    </span>
  );
}

interface CounterProps {
  onDoneAnimating?: (isDone: boolean) => void;
  digitClassName?: string;
  commaClassName?: string;
  value: number;
  fontClassName?: string;
  padding?: number;
  digitHeight?: number;
  places?: PlaceValue[];
  containerStyle?: React.CSSProperties;
  counterStyle?: React.CSSProperties;
  digitStyle?: React.CSSProperties;
  prefix?: string;
  prefixClassName?: string;
  prefixStyle?: React.CSSProperties;
}

export default function Counter({
  commaClassName = "",
  containerStyle,
  counterStyle,
  digitClassName = "",
  digitHeight = 16,
  digitStyle,
  fontClassName = "text-lg font-semibold",
  places = [1000, 100, 10, 1],
  prefix = "",
  prefixClassName = "",
  prefixStyle,
  value,
  onDoneAnimating,
}: CounterProps) {
  const shouldPlaceHaveComma = (place: PlaceValue) => {
    if (place === ".") {
      return false;
    }
    return place === 1000 || place === 1000000;
  };

  return (
    <span className="counter-container" style={containerStyle}>
      <span className="counter-counter" style={{ ...counterStyle }}>
        {prefix && (
          <span className={cn("counter-prefix", fontClassName, prefixClassName)} style={{ ...prefixStyle }}>
            {prefix}
          </span>
        )}
        {places.map((place) => {
          return (
            <Fragment key={place}>
              <Digit
                key={place.toString()}
                place={place}
                value={value}
                height={digitHeight}
                digitStyle={digitStyle}
                fontClassName={fontClassName}
                digitClassName={digitClassName}
                onDoneAnimating={onDoneAnimating}
              />
              {shouldPlaceHaveComma(place) && (
                <span className={cn("counter-comma", fontClassName, commaClassName)}>,</span>
              )}
            </Fragment>
          );
        })}
      </span>
    </span>
  );
}
